import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";

import { db } from "../firebase/config";
import { cards } from "../components/cards";
import NotFound from "./NotFound";
import { prepareCardData } from "../admin/utils/prepareCardData";

const updateMetaTheme = (color) => {
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", color);

  document
    .querySelector('meta[name="msapplication-TileColor"]')
    ?.setAttribute("content", color);
};

const CardPage = () => {
  const { username } = useParams();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load username + profile
  useEffect(() => {
    const loadUser = async () => {
      setLoading(true);
      setUserData(null);

      try {
        if (!username) return;

        const normalizedUsername = username.trim().toLowerCase();

        // Get username document
        const usernameRef = doc(db, "usernames", normalizedUsername);
        const usernameSnap = await getDoc(usernameRef);

        if (!usernameSnap.exists()) return;

        const usernameData = usernameSnap.data();

        const {
          uid,
          active = false,
          createdAt,
        } = usernameData;

        if (!uid) return;

        // Active users are always accessible
        let isValid = active === true;

        // Otherwise, allow access for 1 month after creation
        if (!isValid && createdAt) {
          const createdDate = createdAt.toDate
            ? createdAt.toDate()
            : new Date(createdAt);

          if (!Number.isNaN(createdDate.getTime())) {
            const expirationDate = new Date(createdDate);

            expirationDate.setMonth(
              expirationDate.getMonth() + 1
            );

            if (expirationDate > new Date()) {
              isValid = true;
            }
          }
        }

        // Not active and trial expired
        if (!isValid) return;

        // Get profile
        const profileRef = doc(db, "profiles", uid);
        const profileSnap = await getDoc(profileRef);

        if (!profileSnap.exists()) return;

        setUserData({
          uid,
          ...profileSnap.data(),
        });
      } catch (error) {
        console.error("Error loading profile:", error);
        setUserData(null);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [username]);

  // Prepare card data
  const cardData = userData
    ? prepareCardData(userData)
    : null;

  // Update browser theme color
  useEffect(() => {
    if (!cardData) {
      updateMetaTheme("#ffffff");
      return;
    }

    updateMetaTheme(
      cardData?.selectedTheme?.hex || "#ffffff"
    );
  }, [cardData?.selectedTheme?.hex]);

  // Loading
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-gray-500">
          Loading...
        </div>
      </div>
    );
  }

  // User doesn't exist / expired
  if (!userData) {
    return <NotFound />;
  }

  // Card template
  const cardType =
    cardData?.selectedTemplate || "card1";

  const Card = cards[cardType];

  if (!Card) {
    return <NotFound />;
  }

  return <Card data={cardData} />;
};

export default CardPage;